import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { of, EMPTY } from 'rxjs';
import { filter, debounceTime, distinctUntilChanged, tap, switchMap, catchError } from 'rxjs/operators';
import { FormatPricePipe } from '@pipes/price.pipe';
import { CartService, LineCart } from '@services/cart.service';
import { RouteService } from '@services/route.service';
import { OrderService } from '@services/order.service';
import { Order, Discount } from '@models/order';
import { environment } from '@environments/environment';
import { Price, Product } from 'src/app/models/product';

@Component({
  selector: 'gob-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss']
})
export class CartComponent implements OnInit {
  loading: boolean=false;
  error: string;
  discount: Discount;
  discountControl: FormControl;
  giftWrappingAmount: number=1.50;

  constructor(
    public cart: CartService,
    public route: RouteService,
    private fb: FormBuilder,
    private orderServ: OrderService
  ) {
    this.discountControl=this.fb.control('');
    this.discountControl.valueChanges
      .pipe(
        filter(query => query.length>=3),
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => this.loading=true),
        switchMap(value => this.orderServ.getDiscountCode(value)
          .pipe(
            catchError(() => {
              this.error="Code non valide";
              this.discount=null;
              this.loading=false;
              return EMPTY;
            })
          ))
      )
      .subscribe({
        next: discount => {
          if(discount){
            this.discount=discount;
            this.error=null;
            this.loading=false;
          }
        }
      });
  }

  ngOnInit(): void {
  }

  getImage(product: Product){
    /* return `${environment.imgUrl}/product/${product.images[0].name}`; */
    // There is a cover
    if (product.cover) {
      return `${environment.imgUrl}/product/${product.cover}`;
    }

    // There is a main image
    if (product.mainImage != null) {
      return `${environment.imgUrl}/product/${product.mainImage.name}`;
    } else {
      return `assets/img/logo/logo.png`;
    }
  }

  getTotal(){
    let sum: number=this.cart.getAmount();
    let formatPrice: FormatPricePipe=new FormatPricePipe();
    return formatPrice.transform(sum-this.getDiscount()+this.getGiftWrappingAmount(), true);
  }

  getDiscount(){
    let discount: number=0;
    if(this.discount){
      let sum: number=this.cart.getLines().reduce((acc, current) => {
        if(!current.price.isRemise)
          return acc+=current.qte*current.price.prix;
        else
          return acc;
      }, 0);

      if (sum === 0) {
        this.discount = null;
        this.error = "Le code promotionnel ne peut pas être appliqué aux articles déjà en promotion.";
        return 0;
      }

      if(this.discount.type=='%')
        discount=sum*this.discount.amount/100;
      else{
        discount=this.discount.amount-this.discount.amountUsed;
        if(discount>sum)
          discount=sum;
      }
    }
    return discount;
  }

  /**
   * Returns the number of items in the cart that are on preorder.
   * @returns {number} The number of items in the cart that are on preorder.
   */
  getAvailability(): number {
    let preorder: any = "Précommande";
    return this.cart.getLines().filter((line: { product: { banners: any[]; }; }) => line.product.banners.some(banner => banner.name === preorder)).length;
  }

  next(){
    this.cart.setOrder();
    let order: Order=this.cart.getOrder();
    if(this.discount)
      order.discount=this.discount.codeNumber;
    this.cart.setOrder(order);
    this.route.navigate('cart-shipper');
  }

  /**
   * Sets the gift wrapping amount for the given line item in the cart.
   * If the checkbox is checked, the gift wrapping amount is set to the
   * value of `giftWrappingPrice`. Otherwise, it is set to 0.
   * After setting the gift wrapping amount, the cart is saved.
   * @param {LineCart} line - the line item in the cart to set the gift wrapping amount for.
   * @param {Event} event - the event that triggered this function.
   */
  setGift(line: LineCart, event: Event) {
    const target = event.target as HTMLInputElement;
    line.giftWrapping = target.checked ? 1 : 0;
    this.cart.save();
  }

  /**
   * Returns the total amount of gift wrapping for all items in the cart.
   * This is calculated by summing up the gift wrapping amounts of all items in the cart.
   * @return {number} The total amount of gift wrapping.
   */
  getGiftWrappingAmount(): number{
    let sum: number=this.cart.getLines().reduce((acc: any, current: { giftWrapping: any; }) => {
      if(current.giftWrapping == 1)
        return acc+=this.giftWrappingAmount;
      else
        return acc;
      }, 0);
    return sum;
  }

  /**
   * Decrease the quantity of a product in the cart.
   * If the quantity is already at 1, do nothing.
   * If the quantity is greater than 1, decrease the quantity and save the cart.
   * Also reset the limitReached flag to false when decreasing.
   * @param line The line item to decrease
   */
  decreaseQuantity(line: LineCart) {
    if (line.qte > 1) {
      line.qte--;
      this.cart.save();
    }
    line.maxReached = false;
  }

  /**
   * Increase the quantity of a product in the cart
   * @param line The line item to increase
   * @return void
   * @description
   * If the quantity of the line item is already at the maximum allowed quantity, set the limitReached flag to true and return.
   * Otherwise, increase the quantity of the line item and save the cart.
   */
  increaseQuantity(line: LineCart) {
    const max = this.cart.maxQte(line.product, line.price);

    if (line.qte >= max) {
      line.maxReached = true;
      return;
    }

    line.qte++;
    this.cart.save();
    line.maxReached = false;

  }

  /**
   * Checks if a given release date is in the future.
   * @param releaseDate The release date to check.
   * @returns True if the release date is in the future, false otherwise.
   */
  isFutureRelease(releaseDate: any): boolean {
    if (!releaseDate) return false;
    const date = new Date(releaseDate);
    return date.getTime() > Date.now();
  }

}
